import { Component, HostListener, ViewChild, OnInit } from '@angular/core';
import { GridSettings } from '@app/shared/interface/grid-settings.interface';
import { ODataBindingDirective } from '@app/shared/odata-binding-directive';
import { KendoGridSettingsStorageService } from '@app/shared/services/kendo-grid-settings-storage.service';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { SdsSearchOdataService } from './shared/services/sds-list-odata.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { SdsLocation } from './shared/models/sds-location';
import { SdsService } from './shared/services/sds.service';
import { CustomSDSDetails, SDSDetails } from './shared/models/sds-details';

@Component({
	selector: 'kpa-sds',
	templateUrl: './sds.component.html',
	styleUrls: ['./sds.component.scss'],
})
export class SdsComponent implements OnInit {
	public isDesktop: boolean;
	public isTablet: boolean;
	public isMobile: boolean;
	public locations: any;
	public noDataFoundGridText = '';
	public viewSds: boolean = false;
	public currentSdsDetails: CustomSDSDetails;
	public formats: Array<{ text: string; value: string }> = [{ text: 'GHS', value: 'GHS' }];

	@ViewChild('gridDataBinding', { static: true }) gridDataBinding: ODataBindingDirective;
	private defaultFilter: CompositeFilterDescriptor = {
		logic: 'and',
		filters: [],
	};

	public gridSettings: GridSettings = {
		state: {
			skip: 0,
			take: 10,
			filter: this.defaultFilter,
			sort: [
				{
					field: 'ProductName',
					dir: 'asc',
				},
			],
		},
		columnsConfig: [],
	};

	constructor(
		private readonly kendoGridSettingStorageService: KendoGridSettingsStorageService,
		public readonly sdsSearchService: SdsSearchOdataService,
		private sdsService: SdsService
	) {
		this.updateScreenSize();
		this.getItems = this.getItems.bind(this);
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.updateScreenSize();
	}

	updateScreenSize() {
		const width = window.innerWidth;
		this.isDesktop = width >= 1024;
		this.isTablet = width >= 768 && width < 1024;
		this.isMobile = width < 768;

		// Update grid columns based on screen size
		this.updateGridColumns();
	}

	updateGridColumns() {
		if (this.isDesktop) {
			this.gridSettings.columnsConfig = [
				{
					field: 'ProductName',
					title: 'Product Name',
					id: 'grid_SdsSearch_ProductNameFilter',
					show: true,
					filterable: true,
					width: 300,
					locked: true,
					hidden: false,
					sortable: true,
					minResizableWidth: '250',
				},
				{
					field: 'CommonName',
					title: 'Common Name',
					id: 'grid_SdsSearch_CommonNameFilter',
					show: true,
					filterable: true,
					width: 200,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'Manufacturer',
					title: 'Manufacturer',
					id: 'grid_SdsSearch_ManufacturerFilter',
					show: true,
					filterable: true,
					width: 200,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '150',
				},
				{
					field: 'Appearance',
					title: 'Appearance',
					id: 'grid_SdsSearch_AppearanceFilter',
					show: true,
					filterable: true,
					width: 200,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '150',
				},
				{
					field: 'Keywords',
					title: 'Keywords',
					id: 'grid_SdsSearch_KeywordsFilter',
					show: true,
					filterable: true,
					width: 200,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '150',
				},
				{
					field: 'LocationId',
					title: 'Location',
					id: 'grid_SdsSearch_LocationFilter',
					show: true,
					filterable: true,
					width: 225,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '150',
				},
			];
		} else if (this.isTablet) {
			this.gridSettings.columnsConfig = [
				{
					field: 'ProductName',
					title: 'Product Name',
					id: 'grid_SdsSearch_ProductNameFilter',
					show: true,
					filterable: true,
					width: 160,
					locked: true,
					hidden: false,
					sortable: true,
					minResizableWidth: '120',
				},
				{
					field: 'CommonName',
					title: 'Common Name',
					id: 'grid_SdsSearch_CommonNameFilter',
					show: true,
					filterable: true,
					width: 160,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'Manufacturer',
					title: 'Manufacturer',
					id: 'grid_SdsSearch_ManufacturerFilter',
					show: true,
					filterable: true,
					width: 160,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'Appearance',
					title: 'Appearance',
					id: 'grid_SdsSearch_AppearanceFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'Keywords',
					title: 'Keywords',
					id: 'grid_SdsSearch_KeywordsFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'LocationId',
					title: 'Location',
					id: 'grid_SdsSearch_LocationFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
			];
		} else if (this.isMobile) {
			this.gridSettings.columnsConfig = [
				{
					field: 'ProductName',
					title: 'Product Name',
					id: 'grid_SdsSearch_ProductNameFilter',
					show: true,
					filterable: true,
					width: 130,
					locked: true,
					hidden: false,
					sortable: true,
					minResizableWidth: '100',
				},
				{
					field: 'CommonName',
					title: 'Common Name',
					id: 'grid_SdsSearch_CommonNameFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'Manufacturer',
					title: 'Manufacturer',
					id: 'grid_SdsSearch_ManufacturerFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'Appearance',
					title: 'Appearance',
					id: 'grid_SdsSearch_AppearanceFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'Keywords',
					title: 'Keywords',
					id: 'grid_SdsSearch_KeywordsFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
				{
					field: 'LocationId',
					title: 'Location',
					id: 'grid_SdsSearch_LocationFilter',
					show: true,
					filterable: true,
					width: 100,
					locked: false,
					hidden: false,
					sortable: true,
					minResizableWidth: '80',
				},
			];
		}
	}

	public getItems() {
		return this.sdsSearchService.fetch('SdsList', {
			skip: this.gridSettings.state.skip,
			sort: this.gridSettings.state.sort,
			filter: this.gridSettings.state.filter,
		});
	}

	async ngOnInit() {
		const gridSettings: GridSettings = this.kendoGridSettingStorageService.get('grid2SettingsSds');

		if (gridSettings) {
			this.gridSettings = this.mapGridSettings(gridSettings);
		}

		if (this.hasGridFilters()) {
			this.noDataFoundGridText = '';
		}
		this.initializeDefaultFilters();

		window.scrollTo({ top: 0, behavior: 'smooth' });

		this.setlocations();
	}

	private hasGridFilters = () =>
		!this.gridSettings || !this.gridSettings.state.filter ? false : this.gridSettings.state.filter.filters.length > 0;

	public mapGridSettings(gridSettings: GridSettings): GridSettings {
		const state = gridSettings.state;
		const settings = {
			state,
			columnsConfig: gridSettings.columnsConfig.sort((a, b) => a.orderIndex - b.orderIndex),
		};
		return settings;
	}

	initializeDefaultFilters(): void {
		this.saveGrid();
		this.focusFirstSearchCell();
	}

	focusFirstSearchCell() {
		const element = document.querySelector<HTMLInputElement>('#grid_SdsSearch_ProductNameFilter input');
		if (element) {
			element.focus();
		}
	}

	public onReorder(e: any): void {
		const reorderedColumn = this.gridSettings.columnsConfig.splice(e.oldIndex, 1);
		this.gridSettings.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
		this.saveGrid();
	}

	public onResizeColumn(e: any): void {
		e.forEach((item) => {
			this.gridSettings.columnsConfig.find((col) => col.field === item.column.field).width = item.newWidth;
		});
		this.saveGrid();
	}

	public onVisibilityChange(e: any): void {
		e.columns.forEach((column) => {
			this.gridSettings.columnsConfig.find((col) => col.field === column.field).hidden = column.hidden;
		});
		this.saveGrid();
	}

	public filterChange(filter: CompositeFilterDescriptor): void {
		this.gridSettings.state.filter = filter;
		this.saveGrid();
	}

	saveGrid(): void {
		const gridConfig = {
			state: this.gridSettings.state,
			columnsConfig: this.gridSettings.columnsConfig,
		};
		this.kendoGridSettingStorageService.set('grid2SettingsSds', gridConfig);
	}

	public dataStateChange(state: DataStateChangeEvent): void {
		this.gridSettings.state = state;
		this.saveGrid();
		this.noDataFoundGridText = '';
	}

	public setlocations() {
		this.locations = [];
		this.sdsService.getLocation().subscribe((locations: SdsLocation[]) => {
			locations.forEach((location) => {
				this.locations.push({
					LocationId: location.id,
					textField: location.name,
				});
			});
		});
	}
	createDefaultDetails(): SDSDetails {
		return {
			id: null,
			commonName: null,
			productName: null,
			manufacturer: null,
			appearance: null,
			keywords: null,
			description: null,
			quantity: null,
			category: null,
			nfpa: null,
			hmis: null,
			locations: [],
		};
	}
	populateDetails(data: Partial<SDSDetails>): SDSDetails {
		const details = this.createDefaultDetails();
		return { ...details, ...data };
	}

	onSdsSelect(sdsId: number, fileId: number) {
		this.sdsService.getDetails(sdsId).subscribe((details: SDSDetails) => {
			let sdsDetails = this.populateDetails(details);
			const customDetails: CustomSDSDetails = {
				...sdsDetails,
				isHMISEmpty: sdsDetails.hmis === '_____' || sdsDetails.hmis === null || sdsDetails.hmis === '____/',
				isNFPAEmpty: sdsDetails.nfpa === '____' || sdsDetails.nfpa === null,
				fileId: fileId,
			};
			this.currentSdsDetails = customDetails;
			this.formats = [{ text: 'GHS', value: 'GHS' }];

			if (!this.currentSdsDetails.isHMISEmpty) {
				this.formats.push({ text: 'HMIS', value: 'HMIS' });
			}

			if (!this.currentSdsDetails.isNFPAEmpty) {
				this.formats.push({ text: 'NFPA', value: 'NFPA' });
			}
			this.viewSds = true;
		});
	}
	public closeDialog() {
		this.viewSds = false;
	}
}
